import React, { useCallback, useState, useEffect } from "react";
import { format as dateFormat } from "date-fns";
import { useIntl } from "react-intl";
import { createClasses } from "@bloomberg/media-ui-core";
import {
    Change,
    SecurityName,
    SizedPrice as Price,
    SnapshotChart,
    ChangeSize,
    Size,
    Variant,
} from "@bloomberg/media-ui-markets";
import type { TickerData } from "@bloomberg/media-ui-markets/dist/SnapshotChart/types";

import { fetchSnapshotFromApi } from "~api/client/fetchSnapshotFromApi";
import { PlaceholderImage } from "~components/shared/placeholder";
import type { CuratedItem, Locale, Security } from "~types/types";
import getTickerFromUrl from "~utils/getTickerFromUrl";

import styles from "../Media.module.css";
import { type MediaChildProps, MediaFormat } from "../utils/types";

type SnapshotSecurity = Security & TickerData;

export function hasValidSnapshotChartData(
    source: CuratedItem,
    format: MediaFormat
): boolean {
    if (
        !source?.media?.snapshotChart?.snapshotTicker ||
        format !== MediaFormat.snapshotChart
    ) {
        return false;
    }

    return true;
}

// this component cannot return early if missing data
// due to the hook in useEffect().
//
// The Media parent validates baseline data before
// attempting to render this Media child.
export function SnapshotChartMedia({
    source,
    format,
    config = {},
}: MediaChildProps) {
    const {
        snapshotDuration,
        snapshotTitle,
        snapshotEndDate,
        snapshotStartDate,
        snapshotTicker,
    } = source?.media?.snapshotChart || {};
    const tickerFromUrl = getTickerFromUrl(snapshotTicker) || "";

    const [tickers, setTickers] = useState<SnapshotSecurity[] | null>(null);

    const handleTickerRemoval = useCallback(() => {}, []); // eslint-disable-line @typescript-eslint/no-empty-function
    const timestampFormatter = useCallback(
        (timestamp: number) => {
            if (snapshotDuration === "ONE_DAY") {
                return dateFormat(timestamp, "H:mm");
            }

            if (snapshotDuration === "FIVE_YEAR") {
                return dateFormat(timestamp, "yyyy");
            }

            return dateFormat(timestamp, "M/d");
        },
        [snapshotDuration]
    );

    const intl = useIntl();
    const locale = intl.locale as Locale;

    useEffect(() => {
        fetchSnapshotFromApi({
            ticker: tickerFromUrl,
            duration: snapshotDuration,
            startDate: snapshotStartDate,
            endDate: snapshotEndDate,
            locale,
        }).then((t) => setTickers(t as SnapshotSecurity[]));
    }, []);

    const placeholderClasses = createClasses(styles.Media, styles.placeholder, [
        config.className && config.className,
    ]);

    if (!tickers?.length) {
        return <PlaceholderImage className={placeholderClasses} />;
    }

    const [ticker] = tickers;
    const {
        issuedCurrency,
        issuedUnits,
        longName,
        name,
        type,
        percentChange1Day,
        priceChange,
        price,
    } = ticker;
    const isCurrency = type === "CUR";

    const componentID = `media--${format}`;

    const containerClasses = createClasses(styles.Media, styles.snapshotChart, [
        config.className && config.className,
    ]);

    /* eslint-disable react/jsx-no-target-blank */
    return (
        <div className={containerClasses} data-component={componentID}>
            <SecurityName
                className={styles.securityName}
                size={Size.small}
                securityName={
                    snapshotTitle || (isCurrency ? name : longName) || ""
                }
            />
            <div className={styles.priceRow}>
                <Price
                    size="small"
                    className={styles.price}
                    price={price}
                    currency={(isCurrency ? issuedUnits : issuedCurrency) || ""}
                />{" "}
                <Change
                    size={ChangeSize.small}
                    variant={Variant.refresh2024}
                    percentChange={percentChange1Day}
                    priceChange={priceChange}
                />
            </div>
            <a
                href={`/quote/${tickerFromUrl}`}
                data-link-index="1"
                data-link-related-type="Snapshot"
                target="_blank"
            >
                <SnapshotChart
                    variant="refresh2024"
                    disablePreviousPrice
                    className={styles.chart}
                    altXLabelsCount={3}
                    altYLabelsCount={3}
                    altChartWidth={300}
                    altChartHeight={168}
                    isInteractive={false}
                    altTopPadding={12}
                    altChartAreaPaddingLeft={20}
                    highlightFormatter={timestampFormatter}
                    handleTickerRemoval={handleTickerRemoval}
                    timestampFormatter={timestampFormatter}
                    tickers={tickers}
                    yAxisPosition="right"
                    disableGradient
                />
            </a>
        </div>
    );
}
